import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { EyeIcon } from "@heroicons/react/24/outline";
import { Container, PageHeader, RowAction, Select, Table, Tag } from "../../ui";
import { KINDS, destinationOf, formatKigaliDate, stateOf, STATES, useStaffScholarships } from "../model";

const PAGE_SIZE = 20;
// The states worth filtering by: drafts and cancelled scholarships have no applications to review
const REVIEWABLE = STATES.filter((s) => ["PUBLISHED", "CLOSED", "DECIDED"].includes(s.value));

// "Applicants": pick a scholarship or loan call (`kind`) to see its applications, ranking and award list
export default function ReviewScholarshipsPage({ kind = KINDS.scholarship }) {
  const navigate = useNavigate();
  const [state, setState] = useState("");
  const [page, setPage] = useState(0);
  const { data, isPending, isError } = useStaffScholarships({ state, type: kind.type, page, size: PAGE_SIZE });
  const open = (s) => navigate(`${kind.applicantsPath}/${s.id}`);

  const columns = [
    { key: "name", label: kind.isLoan ? "Loan call" : "Scholarship", render: (s) => <span className="font-medium text-slate-800">{s.name}</span> },
    ...(kind === KINDS.scholarship ? [{ key: "sponsor", label: "Partner", accessor: (s) => s.sponsor?.name ?? "—" }] : []),
    { key: "country", label: "Country", accessor: (s) => destinationOf(s) },
    { key: "academicYear", label: "Academic year", accessor: (s) => s.academicYear?.name ?? "—" },
    { key: "closesAt", label: "Closes", accessor: (s) => formatKigaliDate(s.closedAt ?? s.closesAt), sortValue: (s) => s.closedAt ?? s.closesAt },
    { key: "submittedApplications", label: "Applicants", accessor: (s) => s.submittedApplications ?? 0 },
    { key: "state", label: "State", accessor: (s) => stateOf(s.state).label, render: (s) => <Tag color={stateOf(s.state).color}>{stateOf(s.state).label}</Tag> },
    { key: "open", label: "", exportable: false, render: (s) => <RowAction Icon={EyeIcon} label="Applicants" onClick={() => open(s)} /> },
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title={`${kind.title}: applicants`} />
      <Table cardView
        columns={columns}
        data={data?.content ?? []}
        onRowClick={open}
        isLoading={isPending}
        searchable={false}
        exportName={kind.applicantsPath.slice(1).replaceAll("/", "-")}
        emptyTitle={isError ? `Could not load the ${kind.many}. Please try again later.` : `No ${kind.many} in this state`}
        pagination={{ page, totalPages: data?.totalPages ?? 0, totalElements: data?.totalElements, pageSize: PAGE_SIZE, onPageChange: setPage }}
        filters={
          <Select
            aria-label="State"
            placeholder="All states"
            value={state}
            onChange={(e) => {
              setState(e.target.value);
              setPage(0);
            }}
            options={REVIEWABLE}
            className="w-full sm:w-48"
          />
        }
      />
    </Container>
  );
}
